import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Icon from '../../../components/AppIcon';
import Image from '../../../components/AppImage';
import Button from '../../../components/ui/Button';
import ConfirmationModal from '../../../components/ui/ConfirmationModal';
import { catalogService } from '../../../services/catalogService';

const CatalogCard = ({ catalog, onEdit, onDelete, onView, onStatusChange, onArchive }) => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [showMenu, setShowMenu] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showUnpublishModal, setShowUnpublishModal] = useState(false);
  const [showArchiveModal, setShowArchiveModal] = useState(false);
  const [showShareSuccess, setShowShareSuccess] = useState(false);
  const [showPublishError, setShowPublishError] = useState(false);
  const [publishErrorMessage, setPublishErrorMessage] = useState('');
  const [isUpdatingStatus, setIsUpdatingStatus] = useState(false);
  const [modalError, setModalError] = useState(null);

  const handleEdit = () => {
    navigate(`/catalog-editor?id=${catalog?.id}`);
  };

  const handleViewAnalytics = () => {
    navigate('/analytics-dashboard', { state: { catalogId: catalog?.id } });
  };

  const handleShare = () => {
    const shareUrl = `https://catalogflip.com/view/${catalog?.id}`;
    navigator.clipboard?.writeText(shareUrl);
    setShowShareSuccess(true);
    setShowMenu(false);
    
    // Auto-hide success message after 3 seconds
    setTimeout(() => {
      setShowShareSuccess(false);
    }, 3000);
  };

  const handleDeleteConfirm = () => {
    onDelete(catalog?.id);
    setShowDeleteModal(false);
    setShowMenu(false);
  };

  const handleGetEmbedCode = () => {
    navigate(`/embed-settings/${catalog?.id}`);
  };

  const handlePublish = async () => {
    try {
      setIsUpdatingStatus(true);
      setModalError(null);
      setShowPublishError(false);
      await catalogService?.publishCatalog(catalog?.id);
      onStatusChange?.();
    } catch (error) {
      console.error('Error publishing catalog:', error);
      
      // Check if this is a corrupted image paths error or missing source files error
      if (error?.code === 'CORRUPTED_IMAGE_PATHS' || error?.code === 'ALL_SOURCE_FILES_MISSING') {
        setPublishErrorMessage(
          error?.message || t('dashboard.catalogCard.modals.publishError.message')
        );
        setShowPublishError(true);
      } else {
        setModalError(t('dashboard.catalogCard.errors.publishFailed'));
      }
    } finally {
      setIsUpdatingStatus(false);
    }
  };

  const handleUnpublishConfirm = async () => {
    try {
      setIsUpdatingStatus(true);
      setModalError(null);
      await catalogService?.unpublishCatalog(catalog?.id);
      onStatusChange?.();
      setShowUnpublishModal(false);
    } catch (error) {
      console.error('Error unpublishing catalog:', error);
      setModalError(t('dashboard.catalogCard.errors.unpublishFailed'));
    } finally {
      setIsUpdatingStatus(false);
    }
  };

  const handleArchiveConfirm = async () => {
    try {
      setIsUpdatingStatus(true);
      setModalError(null);
      await catalogService?.archiveCatalog(catalog?.id);
      setShowArchiveModal(false);
      onArchive?.(catalog?.id);
    } catch (error) {
      console.error('Error archiving catalog:', error);
      setModalError(t('dashboard.catalogCard.errors.archiveFailed'));
    } finally {
      setIsUpdatingStatus(false);
    }
  };

  return (
    <>
      <div className="bg-card rounded-lg border border-border overflow-hidden shadow-elevation-sm hover:shadow-elevation-md transition-all duration-250 group">
        <div className="relative aspect-[3/4] overflow-hidden bg-white">
          {catalog?.previewImages?.length > 0 ? (
            <div className="grid grid-cols-2 w-full h-full gap-px bg-border group-hover:scale-105 transition-transform duration-250">
              {catalog.previewImages.slice(0, 4).map((url, idx) => (
                <div key={idx} className="overflow-hidden bg-muted">
                  <Image
                    src={url}
                    alt={`${catalog?.title} - ${idx + 1}`}
                    className="w-full h-full object-cover"
                  />
                </div>
              ))}
              {catalog.previewImages.length < 4 &&
                Array.from({ length: 4 - catalog.previewImages.length }).map((_, idx) => (
                  <div key={`empty-${idx}`} className="bg-muted" />
                ))}
            </div>
          ) : (
            <Image
              src={catalog?.thumbnail}
              alt={catalog?.thumbnailAlt}
              className="w-full h-full object-contain group-hover:scale-105 transition-transform duration-250"
            />
          )}
          <div className={`absolute top-3 right-3 px-2 py-1 rounded-md text-xs font-medium ${
            catalog?.status === 'published' ? 'bg-success/90 text-white' : 'bg-warning/90 text-white'
          }`}>
            {catalog?.status === 'published' ? t('dashboard.catalogCard.statusBadge.published') : t('dashboard.catalogCard.statusBadge.draft')}
          </div>
        </div>

        <div className="p-4">
          <h3 className="text-base md:text-lg font-semibold text-foreground mb-2 line-clamp-2">
            {catalog?.title}
          </h3>

          <div className="flex items-center gap-2 text-xs text-muted-foreground mb-3">
            <Icon name="Calendar" size={14} />
            <span>
              {catalog?.status === 'published' && catalog?.publishedAt
                ? `${t('dashboard.catalogCard.dateLabel.published')} ${new Date(catalog?.publishedAt)?.toLocaleDateString()}`
                : t('dashboard.catalogCard.dateLabel.notPublished')}
            </span>
          </div>

          {/* Actions */}
          <div className="border-t border-border pt-3 flex flex-col gap-2">
            <div className="flex gap-2">
              <Button
                variant="outline"
                size="sm"
                className="flex-1"
                iconName="Eye"
                iconPosition="left"
                onClick={() => onView?.(catalog)}
              >
                {t('dashboard.catalogCard.actions.view')}
              </Button>
              <Button
                variant="outline"
                size="sm"
                className="flex-1"
                iconName="Edit"
                iconPosition="left"
                onClick={() => onEdit?.(catalog)}
              >
                {t('dashboard.catalogCard.actions.edit')}
              </Button>
              {catalog?.status === 'published' && (
                <Button
                  variant="outline"
                  size="sm"
                  iconName="Code"
                  onClick={handleGetEmbedCode}
                  title={t('dashboard.catalogCard.actions.embedCodeTitle')}
                />
              )}
            </div>
            <div className="flex gap-2">
              {catalog?.status === 'published' ? (
                <Button
                  variant="outline"
                  size="sm"
                  className="flex-1"
                  iconName="EyeOff"
                  iconPosition="left"
                  onClick={() => setShowUnpublishModal(true)}
                  loading={isUpdatingStatus}
                >
                  {t('dashboard.catalogCard.actions.unpublish')}
                </Button>
              ) : (
                <Button
                  variant="default"
                  size="sm"
                  className="flex-1"
                  iconName="Upload"
                  iconPosition="left"
                  onClick={handlePublish}
                  loading={isUpdatingStatus}
                >
                  {t('dashboard.catalogCard.actions.publish')}
                </Button>
              )}
              {catalog?.status === 'draft' && (
                <Button
                  variant="ghost"
                  size="sm"
                  iconName="Package"
                  onClick={() => setShowArchiveModal(true)}
                  title={t('dashboard.catalogCard.actions.archive')}
                  loading={isUpdatingStatus}
                />
              )}
              <Button
                variant="ghost"
                size="sm"
                iconName="Trash2"
                onClick={() => setShowDeleteModal(true)}
                className="text-destructive hover:text-destructive"
              />
            </div>
          </div>
        </div>
      </div>

      {/* Share Success Toast */}
      {showShareSuccess && (
        <div className="fixed bottom-4 right-4 z-50 bg-success text-white px-4 py-3 rounded-lg shadow-elevation-lg flex items-center gap-2 animate-in slide-in-from-bottom-5 duration-300">
          <Icon name="Check" size={18} />
          <span className="text-sm font-medium">{t('dashboard.catalogCard.success.linkCopied')}</span>
        </div>
      )}
      
      {/* Publish Error Modal */}
      <ConfirmationModal
        isOpen={showPublishError}
        onClose={() => {
          setShowPublishError(false);
          setPublishErrorMessage('');
        }}
        onConfirm={() => {
          setShowPublishError(false);
          setPublishErrorMessage('');
          setShowDeleteModal(true);
        }}
        title={t('dashboard.catalogCard.modals.publishError.title')}
        message={publishErrorMessage}
        confirmText={t('dashboard.catalogCard.modals.publishError.confirmButton')}
        cancelText={t('dashboard.catalogCard.modals.publishError.cancelButton')}
        variant="destructive"
        icon="AlertTriangle"
      />

      {/* Archive Confirmation Modal */}
      <ConfirmationModal
        isOpen={showArchiveModal}
        onClose={() => {
          setShowArchiveModal(false);
          setModalError(null);
        }}
        onConfirm={handleArchiveConfirm}
        title={t('dashboard.catalogCard.modals.archive.title')}
        message={t('dashboard.catalogCard.modals.archive.description', { title: catalog?.title })}
        confirmText={t('dashboard.catalogCard.modals.archive.confirmButton')}
        cancelText={t('dashboard.catalogCard.modals.archive.cancelButton')}
        variant="warning"
        icon="Package"
        isLoading={isUpdatingStatus}
        error={modalError}
      />

      {/* Delete Confirmation Modal */}
      <ConfirmationModal
        isOpen={showDeleteModal}
        onClose={() => setShowDeleteModal(false)}
        onConfirm={handleDeleteConfirm}
        title={t('dashboard.catalogCard.modals.delete.title')}
        message={t('dashboard.catalogCard.modals.delete.description', { title: catalog?.title })}
        confirmText={t('dashboard.catalogCard.modals.delete.confirmButton')}
        cancelText={t('dashboard.catalogCard.modals.delete.cancelButton')}
        variant="destructive"
        icon="AlertTriangle"
      />

      {/* Unpublish Confirmation Modal */}
      <ConfirmationModal
        isOpen={showUnpublishModal}
        onClose={() => {
          setShowUnpublishModal(false);
          setModalError(null);
        }}
        onConfirm={handleUnpublishConfirm}
        title={t('dashboard.catalogCard.modals.unpublish.title')}
        message={t('dashboard.catalogCard.modals.unpublish.description')}
        confirmText={t('dashboard.catalogCard.modals.unpublish.confirmButton')}
        cancelText={t('dashboard.catalogCard.modals.unpublish.cancelButton')}
        variant="warning"
        icon="EyeOff"
        isLoading={isUpdatingStatus}
        error={modalError}
      />
    </>
  );
};

export default CatalogCard;